import {computed} from 'vue';
import {CompanyIcon, InboxIcon, InvoiceIcon, ManageProjectsIcon, PlusIcon, SettingsIcon} from '@navbar-logged/common/icons';
import {useNotificationsStore} from '@navbar-logged/features/notifications';
import {useUserStore} from '@navbar-logged/features/user';
import {useCurrentHost, useDataTracking, useNuxtApp} from '#imports';
import type {NavigationTab} from '../../aside-menu.types';
import {useFilteredTabs} from '../useFilteredTabs';
import type {NavigationTabsRecord} from './navigation.types';

export function useSubcontractorNavigationTabs(): NavigationTabsRecord {
    const {$i18next} = useNuxtApp();
    const userStore = useUserStore();
    const {isCurrentDomainExtensionOneOf} = useCurrentHost();
    const notificationsStore = useNotificationsStore();
    const logoNavigationRoute = computed(() => '/freelancer/company');
    const {track} = useDataTracking();

    const freelancerOnboarded = computed(() => userStore.user?.options.onboarded);

    const createNavigationTab = computed((): NavigationTab => ({
        id: 'freelancer-create',
        title: $i18next.t('navbar.aside.tabs.title.freelancer.create'),
        icons: {mainIcon: PlusIcon},
        border: true,
        isAlwaysDropdown: true,
        dropdownPlacement: 'bottom',
        children: [
            {
                id: 'freelancer-create-a-quote',
                title: $i18next.t('navbar.aside.tabs.title.freelancer.quote'),
                url: {href: '/project-freelancer/quote/new'},
                action: () => track('quote_started', {source: 'navbar', action_entry_point: 'from_scratch'}),
            },
        ],
    }));

    const freelancerComplianceNotificationCounter = computed(() => {
        if (isCurrentDomainExtensionOneOf(['es', 'fr'])) {
            return notificationsStore.notificationsRecord?.freelancerCompliance;
        }

        return 0;
    });

    const navigationRoutes = computed((): NavigationTab[] => {
        // Error 500 bug randomly appearing https://maltcommunity.slack.com/archives/C03PWC9DPGE/p1683204841095379
        if ($i18next) {
            return [
                createNavigationTab.value,
                {
                    id: 'freelancer-inbox',
                    title: $i18next.t('navbar.aside.tabs.title.freelancer.inbox'),
                    icons: {mainIcon: InboxIcon},
                    url: {href: '/messages'},
                    isActiveWhen: ({startWith}) => startWith('/messages'),
                    notificationsCount: notificationsStore.notificationsRecord?.messages,
                },
                {
                    id: 'freelancer-my-projects',
                    title: $i18next.t('navbar.aside.tabs.title.freelancer.my.projects'),
                    icons: {mainIcon: ManageProjectsIcon},
                    url: {href: '/project-freelancer/?filter=freelancer'},
                    isActiveWhen: ({startWith}: any) => startWith('/project-freelancer'),
                },
                {
                    id: 'freelancer-invoices-payments',
                    title: $i18next.t('navbar.aside.tabs.title.freelancer.invoices.and.payments'),
                    url: {href: '/invoicing/freelancer'},
                    icons: {mainIcon: InvoiceIcon},
                    isActiveWhen: ({startWith}) =>
                        startWith('/invoicing/freelancer') || startWith('/company/payment_in') || startWith('/company-wallet'),
                },
                {
                    separator: 'top',
                    id: 'freelancer-company',
                    title: $i18next.t('navbar.aside.tabs.title.freelancer.company'),
                    url: {href: '/company'},
                    icons: {mainIcon: CompanyIcon},
                    isActiveWhen: ({startWith}) => startWith('/freelancer/company'),
                    notificationsCount: freelancerComplianceNotificationCounter.value,
                },
                {
                    id: 'freelancer-settings',
                    title: $i18next.t('navbar.aside.tabs.title.freelancer.settings'),
                    icons: {mainIcon: SettingsIcon},
                    url: {href: '/account'},
                    isActiveWhen: ({startWith}) => startWith('/account'),
                },
            ];
        }
        return [];
    });

    const allNavigationRoutes = computed((): NavigationTab[] => {
        if (import.meta.client && !freelancerOnboarded.value && window.location.pathname === '/freelancer/company') {
            return navigationRoutes.value.concat([{id: 'navbar-availability', title: ''}]);
        }
        return navigationRoutes.value;
    });

    const {filteredNavigationRoutes} = useFilteredTabs(allNavigationRoutes);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
    };
}
